
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import Button from 'src/components/Button/Button';
import Icon from 'src/components/Icon/Icon';
import './SessionAddTransaction.scss';
import { RootState } from 'src/configs/store';

interface SessionAddTransactionProps {
  isBlack?: boolean,
  showButton?: boolean
}

function SessionAddTransaction({ isBlack = false }: SessionAddTransactionProps) {
  const { transactionTypeList } = useSelector((state: RootState) => state.account);
  const [openMenu, setOpenMenu] = useState(false);
  const openAddTransaction = true;
  const [filterType, setFilterType] = useState(-1);
  const [searchFilter, setSearchFilter] = useState('');
  const [transactionTemplate, setTransactionTemplate] = useState(null);
  const [debitWallet, setDebitWallet] = useState(null);
  const [creditWallet, setCreditWallet] = useState(null);
  const [creditWallets, setCreditWallets] = useState(null);
  const [debitWallets, setDebitWallets] = useState(null);
  const [openDebit, setOpenDebit] = useState(false);
  const [openCredit, setOpenCredit] = useState(false);

  const [payload, setPayload] = useState({
    amount: 0,
    descriptionParams: {},
    currency: 'ZAR',
    divisor: 100,
    transactionType: -1
  });

  const resetState = () => {
    setPayload({
      amount: 0,
      descriptionParams: {},
      currency: 'ZAR',
      divisor: 100,
      transactionType: -1
    });
    setFilterType(-1);
    setSearchFilter('');
    setDebitWallet(null);
    setCreditWallet(null);
    setCreditWallets(null);
    setDebitWallets(null);
    setOpenCredit(false);
    setOpenCredit(false);
  };

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const setInitialState = (transaction: number) => {
    setPayload({
      amount: 0,
      descriptionParams: {},
      currency: 'ZAR',
      divisor: 100,
      transactionType: transaction
    });
  };

  const getDescription = async () => {
    if (filterType !== -1) {
      const template = await dispatch.peripherals.getTransactionDescription(filterType);
      if (template) {
        setTransactionTemplate(template);
        setInitialState(filterType);
      }
    }
  };

  useEffect(() => {
    getDescription();
  }, [filterType]);

  useEffect(() =>  {
    return () => {
      resetState();
    };
  }, []);

  const getWallets = async () => {
    const creditWallet = await dispatch.peripherals.getWallets(transactionTemplate.creditWalletType.id);
    const debitWallet = await dispatch.peripherals.getWallets(transactionTemplate.debitWalletType.id);
    if (creditWallet) {
      setDebitWallet(creditWallet[0].id);
      setCreditWallet(debitWallet[0].id);
      setCreditWallets(creditWallet);
      setDebitWallets(debitWallet);
    }
  };

  const handleTypeChange = (e) => {
    resetState();
    const newTransaction = _.find(transactionTypeList, { code: +(e.target.value) });
    if (newTransaction) {
      setSearchFilter((newTransaction.code).toString());
      setFilterType(newTransaction.id);
    }
  };

  useEffect(() => {
    if ((!creditWallets || creditWallets.length === 0) && transactionTemplate) {
      getWallets();
    }
  }, [transactionTemplate]);

  const handleSubmit = () => {
    const newTransaction = _.find(transactionTypeList, { code: +searchFilter });
    if (newTransaction) {
      setSearchFilter((newTransaction.code).toString());
      setFilterType(newTransaction.id);
    }
  };

  const handleChange = (e) => {
    if (e.target.name !== 'amount') {
      setPayload({ ...payload, descriptionParams: { ...payload.descriptionParams, [e.target.name]: e.target.value } });
    } else {
      setPayload({ ...payload, [e.target.name]: e.target.value });
    }
  };

  const submitPayload = async (e) => {
    e.preventDefault();
    let index = 0;

    const transactionParameters = _.map(payload.descriptionParams, (param) => {
      const id = transactionTemplate?.transactionTypeParameterTemplates[index].id;
      index += 1;

      return (
        { transactionParameterTemplate: id, value: param }
      );
    });
    delete payload.descriptionParams;

    await dispatch.peripherals.addTransaction({
      ...payload,
      amount: payload.amount,
      creditWallet: creditWallet,
      debitWallet: debitWallet,
      transactionParameters
    });

    setPayload({
      amount: 0,
      descriptionParams: {},
      currency: 'ZAR',
      divisor: 100,
      transactionType: -1
    });
    setTransactionTemplate(null);
    setFilterType(-1);
    dispatch.peripherals.changeReload(true);
  };

  const snakeToNormal = str =>
    str.toLowerCase().replace(/([-_][a-z])/g, group =>
      group
        .toUpperCase()
        .replace('-', ' ')
        .replace('_', ' ')
    );

  return (
    <>
      <div className={
        classNames({
          'add-transaction session-transaction__container': true,
          'session-transaction__black': isBlack
        })
      }
      >
        <div className="session-transaction__sub-title">{ t('transactions.selectTransactionTypes') }</div>
        {
          openAddTransaction &&
          <div className="session-transaction__transaction-type">
            <div className="session-transaction__search" onClick={ () => setOpenMenu(!openMenu) }>
              <h3>
                {
                  transactionTypeList && _.find(transactionTypeList, { id: +filterType }) ?
                    ` ${ _.find(transactionTypeList, { id: +filterType }).code } -
                  ${ _.find(transactionTypeList, { id: +filterType }).name } ` : t('transactions.transactionType')
                }
              </h3>
              <Icon
                className={
                  classNames({
                    'session-transaction__icon': true,
                    'session-transaction__icon--closed': !openAddTransaction
                  })
                }
                icon="dropdownArrow"
              />
              {
                openMenu &&
                <ClickAwayListener onClickAway={ () => setOpenMenu(false) }>
                  <ul className="session-transaction__type">
                    <li
                      className={
                        classNames({
                          'session-transaction__item': true,
                          'session-transaction__item--selected': +filterType === -1
                        })
                      }
                      onClick={ handleTypeChange }
                      value={ -1 }
                    >{ t('transactions.all') }</li>
                    {
                      _.map(transactionTypeList, (transaction) => {
                        return (
                          <li
                            className={
                              classNames({
                                'session-transaction__item': true,
                                'session-transaction__item--selected': +filterType === transaction.id
                              })
                            }
                            onClick={ handleTypeChange }
                            key={ transaction.id }
                            value={ transaction.code }
                          >
                            { `${ transaction.code } - ${ transaction.name }` }
                          </li>
                        );
                      })
                    }
                  </ul>
                </ClickAwayListener>
              }
            </div>
            <input
              autoComplete="new-password"
              className="session-transaction__search-input"
              type="text"
              value={ searchFilter }
              onChange={ (e) => setSearchFilter(e.target.value) }
            />
            <Button
              type="button"
              onClick={ handleSubmit }
              size="extra-small"
              className="btn--primary-revert session-transaction__button"
            >{ t('transactions.go') }</Button>
          </div>
        }

        {
          openAddTransaction &&
          <>
            { transactionTemplate && <div className="session-transaction__sub-title">{ t('transactions.configTransactions') }</div> }

            <form className="session-transaction__inputs" onSubmit={ () => { } }>
              {
                transactionTemplate &&
                <>
                  <h4 className="session-transaction__inputs-header">{ t('transactions.code') }</h4>
                  <h4 className="session-transaction__inputs-header">{ t('transactions.name') }</h4>
                  <h4 className="session-transaction__inputs-header">{ t('transactions.debitWallet') }</h4>
                  <h4 className="session-transaction__inputs-header">{ t('transactions.creditWallet') }</h4>
                  <h4 className="session-transaction__inputs-header">{ t('transactions.amount') }</h4>
                  <h4 className="session-transaction__inputs-header">{ t('transactions.currency') }</h4>
                  <>
                    <span className="session-transaction__inputs-info">{ transactionTemplate.code }</span>
                    <div className="session-transaction__params-container">
                      <div className="session-transaction__inputs-info">{ `${ transactionTemplate.name }` }</div>
                    </div>
                    <div className="session-transaction__params-container">
                      <div className="session-transaction__search session-transaction__search--wallets" onClick={ () => setOpenDebit(!openDebit) }>
                        <h3>
                          {
                            _.find(debitWallets, { id: debitWallet }) ?
                              ` ${ _.find(debitWallets, { id: debitWallet }).id } -
                  ${ _.find(debitWallets, { id: debitWallet }).name } ` : t('transactions.debitWallet')
                          }
                        </h3>
                        <Icon
                          className={
                            classNames({
                              'session-transaction__icon': true,
                              'session-transaction__icon--closed': !openDebit
                            })
                          }
                          icon="dropdownArrow"
                        />
                        {
                          openDebit &&
                            <ClickAwayListener onClickAway={ () => setOpenDebit(false) }>
                              <ul className="session-transaction__type session-transaction__type--wallets">
                                {
                                  _.map(debitWallets, (wallet) => {
                                    return (
                                      <li
                                        className={
                                          classNames({
                                            'session-transaction__item session-transaction__item--wallets': true
                                          })
                                        }
                                        onClick={ () => setDebitWallet(wallet.id) }
                                        key={ wallet.id }
                                        value={ wallet.id }
                                      >
                                        { `${ wallet.id } - ${ wallet.name }` }
                                      </li>
                                    );
                                  })
                                }
                              </ul>
                            </ClickAwayListener>
                        }
                      </div>
                    </div>
                    <div className="session-transaction__params-container">
                      <div className="session-transaction__search session-transaction__search--wallets" onClick={ () => setOpenCredit(!openCredit) }>
                        <h3>
                          {
                            _.find(creditWallets, { id: creditWallet }) ?
                              ` ${ _.find(creditWallets, { id: creditWallet }).id } -
                  ${ _.find(creditWallets, { id: creditWallet }).name } ` : t('transactions.creditWallet')
                          }
                        </h3>
                        <Icon
                          className={
                            classNames({
                              'session-transaction__icon': true,
                              'session-transaction__icon--closed': !openCredit
                            })
                          }
                          icon="dropdownArrow"
                        />
                        {
                          openCredit &&
                            <ClickAwayListener onClickAway={ () => setOpenCredit(false) }>
                              <ul className="session-transaction__type session-transaction__type--wallets">
                                {
                                  _.map(creditWallets, (wallet) => {
                                    return (
                                      <li
                                        className={
                                          classNames({
                                            'session-transaction__item session-transaction__item--wallets': true
                                          })
                                        }
                                        onClick={ () => setCreditWallet(wallet.id) }
                                        key={ wallet.id }
                                        value={ wallet.id }
                                      >
                                        { `${ wallet.id } - ${ wallet.name }` }
                                      </li>
                                    );
                                  })
                                }
                              </ul>
                            </ClickAwayListener>
                        }

                      </div>
                    </div>
                    <input
                      className="session-transaction__search-input"
                      type="text"
                      autoComplete="new-password"
                      name="amount"
                      value={ payload.amount }
                      onChange={ handleChange }
                    />
                    <span className="session-transaction__inputs-info">ZAR</span>
                  </>
                </>
              }
            </form>
            {
              transactionTemplate &&
              <>
                <form className="session-transaction__inputs session-transaction__inputs--bottom">
                  <h4 className="session-transaction__inputs-header">
                    { t('transactions.description') }
                  </h4>
                  {
                    _.map(Array.from(Array(6).keys()), (array, index) => {
                      if (transactionTemplate?.transactionTypeParameterTemplates[index]) {
                        return (
                          <h4
                            key={ array }
                            className="session-transaction__inputs-header"
                          >{ snakeToNormal(transactionTemplate.transactionTypeParameterTemplates[index].name) }</h4>

                        );
                      } else {
                        return (
                          <span />);
                      }
                    })
                  }
                  <div className="session-transaction__params-container">
                    <div>{ transactionTemplate?.description }</div>
                  </div>
                  {
                    _.map(Array.from(Array(5).keys()), (array, index) => {
                      if (transactionTemplate?.transactionTypeParameterTemplates[index]) {
                        return (
                          <input
                            key={ transactionTemplate?.transactionTypeParameterTemplates[index].id }
                            autoComplete="new-password"
                            className="session-transaction__search-input"
                            type={ transactionTemplate?.transactionTypeParameterTemplates[index].format === 'string' ? 'text' : 'number' }
                            name={ transactionTemplate?.transactionTypeParameterTemplates[index].name }
                            onChange={ handleChange }
                            value={ payload.descriptionParams[transactionTemplate?.transactionTypeParameterTemplates[index].name] }
                            placeholder={ transactionTemplate?.transactionTypeParameterTemplates[index].name }
                            required
                          />

                        );
                      } else {
                        return (
                          <span />);
                      }
                    })
                  }
                </form>
                <div className="session-transaction__buttons-container">

                  <Button
                    onClick={ submitPayload }
                    size="extra-small"
                    className="btn--cta session-transaction__button session-transaction__button--submit"
                  >
                    { t('transactions.submit') }
                  </Button>
                </div>
              </>
            }
          </>
        }
      </div>
    </>
  );
}

export default SessionAddTransaction;
